import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { i18n } from '@wms/shared';
import { api, ApiError } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';

const { t } = i18n;

interface SignedDocument {
  key: string;
  url: string | null;
  filename: string;
  contentType: string;
  bytes: number;
  uploadedAt: string;
}

interface UploadUrlResponse {
  uploadUrl: string;
  objectKey: string;
  headers: Record<string, string>;
}

const MAX_BYTES = 25 * 1024 * 1024; // must match the API / shared schema

interface Notice {
  kind: 'success' | 'error';
  message: string;
}

function uploadFailureReason(error: unknown): string {
  if (error instanceof ApiError) return error.code;
  if (error instanceof Error && error.message.trim()) return error.message;
  return t('order.documents.unknown_error');
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * PDF document attachments for an order (WMS-28). Same presigned-PUT
 * choreography as PhotoGallery (upload-url → PUT to MinIO → confirm), but
 * renders a list of downloadable links and accepts PDFs only. Supports
 * attaching several files at once and deleting individual ones. Managing is
 * allowed at any order status; when `canEdit` is false it's view/download only.
 */
export function OrderDocuments({
  orderId,
  canEdit,
  reloadKey = 0,
}: {
  orderId: string;
  canEdit: boolean;
  reloadKey?: number;
}) {
  const basePath = `/orders/${orderId}/documents`;
  const [docs, setDocs] = useState<SignedDocument[] | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalKey, setInternalKey] = useState(0);

  useEffect(() => {
    setDocs(null);
    api<{ documents: SignedDocument[] }>(`${basePath}/list`, { method: 'POST' })
      .then((r) => setDocs(r.documents))
      .catch(() => setDocs([]));
  }, [basePath, reloadKey, internalKey]);

  async function uploadOne(file: File): Promise<void> {
    // 1. Presigned PUT URL.
    const presigned = await api<UploadUrlResponse>(`${basePath}/upload-url`, {
      method: 'POST',
      body: { contentType: file.type, sizeBytes: file.size },
    });
    // 2. Bytes straight to MinIO (no CSRF/credentials on the third-party PUT).
    const putRes = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      headers: presigned.headers,
      body: file,
    });
    if (!putRes.ok) throw new Error(`upload PUT failed (${putRes.status})`);
    // 3. Confirm — server records it into the order's documents JSON.
    await api<{ documents: SignedDocument[] }>(`${basePath}/confirm`, {
      method: 'POST',
      body: {
        objectKey: presigned.objectKey,
        contentType: file.type,
        sizeBytes: file.size,
        filename: file.name,
      },
    });
  }

  async function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file later
    if (files.length === 0) return;
    setNotice(null);

    for (const file of files) {
      if (file.type !== 'application/pdf') {
        setNotice({ kind: 'error', message: t('order.documents.unsupported_type') });
        return;
      }
      if (file.size > MAX_BYTES) {
        setNotice({ kind: 'error', message: t('order.documents.too_large') });
        return;
      }
    }

    setBusy(true);
    try {
      for (const file of files) await uploadOne(file);
      setInternalKey((k) => k + 1);
      setNotice({ kind: 'success', message: t('order.documents.upload_succeeded') });
    } catch (uploadError) {
      setNotice({
        kind: 'error',
        message: `${t('order.documents.upload_failed')} ${t('order.documents.failure_reason')}: ${uploadFailureReason(uploadError)}`,
      });
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(key: string) {
    if (!window.confirm(t('order.documents.confirm_delete'))) return;
    setBusy(true);
    setNotice(null);
    try {
      const r = await api<{ documents: SignedDocument[] }>(`${basePath}/delete`, {
        method: 'POST',
        body: { objectKey: key },
      });
      setDocs(r.documents);
    } catch {
      setNotice({ kind: 'error', message: t('order.documents.delete_failed') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('order.documents.title')}
        </h3>
        {canEdit ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={(e) => void onFiles(e)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('order.documents.add')}
            </button>
          </>
        ) : null}
      </div>
      {notice ? (
        <div
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={`flex items-start justify-between gap-3 rounded border px-3 py-2 text-sm ${
            notice.kind === 'error'
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-green-300 bg-green-50 text-green-700'
          }`}
        >
          <span>{notice.message}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label={t('common.close')}
            className="shrink-0 text-lg leading-none opacity-70 hover:opacity-100"
          >
            ×
          </button>
        </div>
      ) : null}
      {docs === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : docs.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          {t('order.documents.empty')}
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {docs.map((d) => (
            <li key={d.key} className="flex items-center justify-between gap-3 px-3 py-2">
              <a
                href={d.url ?? '#'}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 items-center gap-2 text-sm text-primary hover:underline"
              >
                <span aria-hidden className="shrink-0">
                  📄
                </span>
                <span className="truncate">{d.filename}</span>
              </a>
              <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                <span className="tabular-nums">{formatBytes(d.bytes)}</span>
                <span className="hidden sm:inline">{formatDateTime(d.uploadedAt)}</span>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => void onDelete(d.key)}
                    disabled={busy}
                    aria-label={t('common.delete')}
                    className="text-red-600 hover:text-red-700 disabled:opacity-40"
                  >
                    ×
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
